import { defineStore } from 'pinia';
import { useApi } from '~/composables/useApi';

/** 서버에 없는 기본 아바타 URL — img 요청 시 404 방지 */
const isUsableProfileImageUrl = (url?: string | null): url is string => {
  if (!url || !url.trim()) return false;
  return !url.includes('default-avatar.png');
};

interface UserImgsState {
  userImages: Record<string, string>;
  isInitialized: boolean;
  isLoading: boolean;
}

export const useUserImgsStore = defineStore('userImgs', {
  state: (): UserImgsState => ({
    userImages: {},
    isInitialized: false,
    isLoading: false
  }),

  getters: {
    getUserImage:
      state =>
      (userId: string): string => {
        return state.userImages[userId] || '';
      }
  },

  actions: {
    async initializeUserImages() {
      console.log('initializeUserImages called', {
        isInitialized: this.isInitialized,
        isLoading: this.isLoading
      });

      if (this.isInitialized || this.isLoading) {
        //console.log('Early return: already initialized or loading');
        return;
      }

      this.isLoading = true;
      //console.log('Starting to load user images');

      try {
        //alert(1);
        const data = await useApi<{ imageUrlList: Array<{ userId: string; imageUrl: string }> }>(
          '/api/cdn/images/userImgList',
          {
            method: 'GET',
            skipLoading: true
          }
        );
        if (data?.imageUrlList) {
          const userImagesMap: Record<string, string> = {};
          data.imageUrlList.forEach(item => {
            if (isUsableProfileImageUrl(item.imageUrl)) {
              userImagesMap[item.userId] = item.imageUrl;
            }
          });
          this.userImages = userImagesMap;
        }

        this.isInitialized = true;
      } catch (error) {
        console.warn('Failed to fetch user images:', error);
      } finally {
        this.isLoading = false;
      }
    },

    async fetchUserImage(userId: string): Promise<string> {
      if (!userId) {
        return '';
      }

      // '미조회' 와 '조회했는데 이미지가 없음('')' 을 반드시 구분해야 한다.
      // 값으로만 판단하면 이미지가 없는 사용자는 매번 다시 요청하게 되고,
      // 조직도 검색처럼 행이 대량으로 다시 그려질 때마다 같은 요청이 수십 건씩 몰린다.
      if (userId in this.userImages) {
        return this.userImages[userId];
      }

      try {
        const data = await useApi<{ imageUrl: string }>(`/api/cdn/images/userImg/${userId}`, {
          method: 'GET',
          skipLoading: true,
          // 프로필 이미지는 없어도 화면이 도는 보조 정보다. 실패를 토스트로 알리지 않는다.
          skipErrorToast: true
        });

        if (isUsableProfileImageUrl(data?.imageUrl)) {
          this.userImages[userId] = data.imageUrl;
          return data.imageUrl;
        }
      } catch (error) {
        console.warn(`Failed to fetch user image for ${userId}:`, error);
      }

      this.userImages[userId] = '';
      return '';
    },

    clearUserImages() {
      this.userImages = {};
      this.isInitialized = false;
      this.isLoading = false;
    }
  }
});
